import { useState, useEffect } from 'react';
import { FiX, FiLoader, FiCheckCircle } from 'react-icons/fi';
import { usePersona } from '../../contexts/PersonaContext';
import './PersonaModal.css';

const PersonaModal = ({ isOpen, onClose }) => {
  const { persona, updatePersona, resetPersona, isLoading, promptsJustLoaded } = usePersona();
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Local state for form values
  const [formValues, setFormValues] = useState({
    job_title: '',
    industry: '',
    experience_level: '',
    use_case: ''
  });

  // Update local state when persona changes
  useEffect(() => {
    if (persona) {
      setFormValues({
        job_title: persona.job_title || '',
        industry: persona.industry || '',
        experience_level: persona.experience_level || '',
        use_case: persona.use_case || ''
      });
    }
  }, [persona]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSaving(true);
    setShowSuccess(true);
    updatePersona(formValues);
  };

  // Handle reset
  const handleReset = () => {
    setIsSaving(true);
    setShowSuccess(true);
    resetPersona();
  };

  // Close modal when loading is complete
  useEffect(() => {
    if (isSaving && !isLoading && promptsJustLoaded) {
      // Show success animation for a moment before closing
      const timer = setTimeout(() => {
        setIsSaving(false);
        setShowSuccess(false);
        onClose();
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [isLoading, isSaving, onClose, promptsJustLoaded]);

  // If modal is not open, don't render anything
  if (!isOpen) return null;

  return (
    <div className="persona-modal-overlay">
      <div className="persona-modal">
        <div className="persona-modal-header">
          <h2>Customize Your Persona</h2>
          <button className="close-button" onClick={onClose} disabled={isSaving || isLoading}>
            <FiX size={24} />
          </button>
        </div>
        
        <div className="persona-modal-content">
          {showSuccess && promptsJustLoaded ? (
            <div className="persona-success-message">
              <div className="success-checkmark">
                <div className="check-icon"></div>
              </div>
              <h3>Personalized Prompts Generated!</h3>
              <p>Your custom prompts are ready to use.</p>
            </div>
          ) : (
            <>
              <p className="persona-description">
                Customize your persona to get personalized diagram suggestions and prompts
                tailored to your role, industry, and experience level. When you save your settings,
                we'll generate custom prompts specifically for your needs.
              </p>
              
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="job_title">Job Title</label>
                  <input
                    type="text"
                    id="job_title"
                    name="job_title"
                    value={formValues.job_title}
                    onChange={handleChange}
                    placeholder="e.g. Product Manager, Software Engineer"
                    required
                    disabled={isSaving || isLoading}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="industry">Industry</label>
                  <select
                    id="industry"
                    name="industry"
                    value={formValues.industry}
                    onChange={handleChange}
                    required
                    disabled={isSaving || isLoading}
                  >
                    <option value="">Select an industry</option>
                    <option value="Technology">Technology</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Finance">Finance</option>
                    <option value="Education">Education</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Retail">Retail</option>
                    <option value="Government">Government</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="experience_level">Experience Level</label>
                  <select
                    id="experience_level"
                    name="experience_level"
                    value={formValues.experience_level}
                    onChange={handleChange}
                    required
                    disabled={isSaving || isLoading}
                  >
                    <option value="">Select experience level</option>
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Expert">Expert</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="use_case">Primary Use Case (Optional)</label>
                  <input
                    type="text"
                    id="use_case"
                    name="use_case"
                    value={formValues.use_case}
                    onChange={handleChange}
                    placeholder="e.g. Project Planning, Technical Documentation"
                    disabled={isSaving || isLoading}
                  />
                </div>
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={handleReset}
                    disabled={isSaving || isLoading}
                  >
                    Reset to Default
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={isSaving || isLoading}
                  >
                    {isSaving && isLoading ? 'Generating Prompts...' : 
                     isSaving ? 'Saving...' : 
                     'Save & Generate Prompts'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
      
      {/* Loading spinner */}
      {(isSaving || isLoading) && !promptsJustLoaded && (
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default PersonaModal; 